import { Injectable } from '@angular/core';
import { Solution } from 'src/app/models/service/solution';
import { SolutionVersionsInfo } from 'src/app/models/service/solution-versions-info';
import { Instance } from 'src/app/models/instance/instance';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { SemanticVersioningService } from 'src/app/services/util/semantic-versioning.service';
import { SolutionStatus } from 'src/app/models/service/solution-status';
import { DataZone } from '../../models/data-zone/data-zone';
import { DeployStatus } from '../../models/deploy/deploy-status';
import { DeploymentClass } from '../../models/instance-class';

@Injectable({
  providedIn: 'root'
})
export class SolutionVersionsService {

  constructor(private semanticVersioningService: SemanticVersioningService) {

  }

  /**
   * Calcualtes service versions information holding information for current service version,
   * latest published version and latest coming soon version.
   *
   * @param service for which we want to get the versions info
   * @param serviceVersions all service versions
   * @param instances all instances for a given blueprint.
   */
  getSolutionVersionsInfo(service: Solution, serviceVersions: SolutionVersion[], instances: Instance[]): SolutionVersionsInfo {
    const serviceVersionsMap = serviceVersions.reduce((acc, solutionVersion) => {
      acc[solutionVersion.id] = solutionVersion;
      return acc;
    }, {});

    let liveInstance = null;
    let devVersion = null;

    instances.forEach(instance => {
      if (instance.dataZone === DataZone.LIVE && instance.status === DeployStatus.DEPLOYED) {
        liveInstance = instance;
      }
      if (instance.deploymentClass === DeploymentClass.D && instance.status === DeployStatus.DEPLOYED) {
        devVersion = serviceVersionsMap[instance.solutionVersionId];
      }
    });

    let currentVersion: SolutionVersion;
    if (!liveInstance) {
      currentVersion = serviceVersionsMap[service?.latestSolutionVersionId];
      return { currentVersion, devVersion, comingSoonVersion: null, updateVersion: null };
    } else {
      currentVersion = serviceVersionsMap[liveInstance.solutionVersionId];
    }

    const updateVersion = serviceVersions.reduce<SolutionVersion>((latestVersion, serviceVersion) => {
      if (serviceVersion.status === SolutionStatus.PUBLISHED
        && currentVersion
        && currentVersion.id !== serviceVersion.id
        && this.semanticVersioningService.compareVersions(currentVersion.version, serviceVersion.version)
        && (!latestVersion || this.semanticVersioningService.compareVersions(latestVersion.version, serviceVersion.version))) {
          latestVersion = serviceVersion;
      }
      return latestVersion;
    }, undefined);

    const comingSoonVersion = serviceVersions.reduce<SolutionVersion>((latestVersion, serviceVersion) => {
      if (serviceVersion.status === SolutionStatus.COMING_SOON
        && currentVersion.id !== serviceVersion.id
        && this.semanticVersioningService.compareVersions(currentVersion.version, serviceVersion.version)
        && (!latestVersion || this.semanticVersioningService.compareVersions(latestVersion.version, serviceVersion.version))) {
          latestVersion = serviceVersion;
      }
      return latestVersion;
    }, undefined);

    return { currentVersion, updateVersion, comingSoonVersion, devVersion };
  }

  /**
   * Check the release version for AMR
   * @param releaseVersionText - the release version
   * @returns - retuns true if condition satisfy
   */
  releaseVersion(releaseVersionText): boolean {
    const verifyVersion = releaseVersionText?.split('.');
    if (verifyVersion) {
      releaseVersionText = verifyVersion[0];
      if(releaseVersionText.length === 2 || (releaseVersionText.startsWith('R') && releaseVersionText.length === 3)){
        return true;
      }
    }
  }

}
